import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import lowerCase from 'lodash/lowerCase';
import pluralize from 'pluralize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { Link, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { CertificatesSelectPaginate } from '@/modules/common/components/certificates/CertificatesSelectPaginate.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import ReportsSelectPaginate, {
  ReportOption,
} from '@/modules/issuer-dashboard/components/ReportsSelectPaginate.tsx';
import { useIssuerAddAnnouncementMutation } from '@/modules/issuer-dashboard/queries/communications.ts';
import { useShareholdersByIdsSubscriptionsClasses } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  BaseModalProps,
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import { replaceQuillSizeOptionIcons } from 'ui/utils';
import { ShareCertificateResponse } from 'api';
import { CreateCommunicationRequest } from 'api/generated';
import 'react-quill/dist/quill.snow.css';

const validationSchema = yup.object({
  subject: yup.string().trim().min(5).max(50).required('This field is required'),
  text: yup.string().trim(),
  shareholder_ids: yup
    .array()
    .min(1, 'Select at least one holder')
    .required('This field is required'),
});

const initialValues: CreateCommunicationRequest = {
  subject: '',
  text: '',
  report_ids: [],
  shareholder_ids: [],
};

const FontSizeStyle = Quill.import('attributors/style/size') as any;

FontSizeStyle.whitelist = ['10px', '14px', '18px', '24px', '32px'];
Quill.register(FontSizeStyle, true);

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'size',
  'script',
  'header',
  'list',
  // 'bullet',
  'link',
];

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: '24px' }, { size: '32px' }],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const allClassesOption = { value: 0, label: 'All classes' };

export interface IssuerAddAnnouncementModalProps extends BaseModalProps {
  reports?: ReportOption[];
  certificates?: SelectPaginateOption<ShareCertificateResponse>[];
  registerClass?: SelectPaginateOption<number> | null;
}

export const IssuerAddAnnouncementModal = ({
  open,
  onClose,
  registerClass = allClassesOption,
  reports = [],
  certificates = [],
}: IssuerAddAnnouncementModalProps) => {
  const [params, setParams] = useSearchParams();
  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const [selectedReports, setSelectedReports] = useState(reports);
  const [selectedCertificates, setSelectedCertificates] = useState(certificates);
  const [selectedClass, setSelectedClass] = useState<SelectPaginateOption<number> | null>(
    registerClass,
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await handleSubmit({
        ...values,
        report_ids: selectedReports?.map(({ value }) => value.id),
        certificate_ids: selectedCertificates?.map(({ value }) => value.id),
        text: `<div style="text-align: initial">
                  ${values.text
                    .replaceAll('<p>', "<p style='margin: 0'>")
                    .replaceAll('<h1>', "<h1 style='margin: 0'>")
                    .replaceAll('<h2>', "<h2 style='margin: 0'>")
                    .replaceAll('<ul>', "<ul style='margin: 0'>")
                    .replaceAll('<li>', "<li style='margin: 0'>")}
                </div>`,
      });
    },
    enableReinitialize: true,
  });

  const handleSubmit = async (values: CreateCommunicationRequest) => {
    await addAnnouncementEvent(values);
    formik.resetForm();
    onClose();
  };

  const { data: registerHoldersData } = useRegisterShareholderList({
    register_id: registerId,
    is_subscribed: true,
  });

  const registerHolders = useMemo(
    () => registerHoldersData?.shareholders || [],
    [registerHoldersData?.shareholders],
  );

  const checkedAllHoldersCheckbox =
    formik.values.shareholder_ids.length === registerHolders.length &&
    formik.values.shareholder_ids.length > 0;
  const indeterminateAllHoldersCheckbox =
    !checkedAllHoldersCheckbox && !!formik.values.shareholder_ids.length;

  const { isFetching: isClassHoldersLoading, data: classHoldersData } = useRegisterShareholderList(
    {
      register_id: registerId,
      register_class_id: selectedClass?.value || undefined,
      is_subscribed: true,
    },
    { disabled: !selectedClass },
  );

  const {
    data: holdersSubscriptions,
  } = // todo: get rid of it?
    useShareholdersByIdsSubscriptionsClasses(
      { shareholders_ids: formik.values.shareholder_ids.toString() },
      !formik.values.shareholder_ids.length,
    );

  const handleChangeClass = useCallback(
    (item: SelectPaginateOption<number> | null) => {
      setSelectedClass(item);
      if (!classHoldersData || !item) return;

      formik.setFieldValue(
        'shareholder_ids',
        item?.value === 0 ? [] : classHoldersData.shareholders.map(holder => holder.id),
      );
    },
    [classHoldersData],
  );

  const { data: registersResponse, isLoading: isRegistersLoading } =
    useCurrentUserRegistersQuery(true);

  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: registerId },
    true,
  );

  const { mutateAsync: addAnnouncementEvent, isLoading: isAnnouncementCreation } =
    useIssuerAddAnnouncementMutation({
      setErrors: formik.setErrors,
    });

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return (
      registersResponse?.data.map(({ register_id, register_obj }) => ({
        label: register_obj?.name,
        value: register_id,
      })) || []
    );
  }, [registersResponse]);

  const handleAsyncRegisterClassList = useCallback(
    async (currentPage: number, search?: string) => {
      const response = await fetchNextRegisterClassListPage({
        pageParam: {
          page: currentPage,
          search,
        },
      });

      return response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id }) => ({
          label: class_name,
          value: id,
        }));
      }, []);
    },
    [fetchNextRegisterClassListPage],
  );

  const handleChangeRegister = useCallback(
    (register_id: number | null) => {
      if (!register_id) return;

      formik.setFieldValue('register_class_id', null);
      formik.setFieldValue('shareholder_ids', []);
      setSelectedClass(null);
      setSelectedReports([]);

      setParams(params => {
        params.set(REGISTER_ID_SEARCH_PARAM_KEY, register_id?.toString());
        return params;
      });
    },
    [formik, setParams],
  );

  const handleCloseModalWithResetForm = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const handleChangeSubject = useCallback(
    async (value: string) => {
      await formik.setFieldValue('subject', value);
      formik.validateField('subject');
    },
    [formik],
  );

  const handleChangeText = useCallback(
    (value: string) => {
      formik.setFieldValue('text', value);
    },
    [formik],
  );

  const handleToggleAllHoldersSelection = useCallback(
    async (checked: boolean) => {
      setSelectedClass(checked ? allClassesOption : null);

      const newHolders = checked ? registerHolders.map(({ id }) => id) : [];

      await formik.setFieldValue('shareholder_ids', newHolders);
      await formik.validateField('shareholder_ids');
    },
    [formik, registerHolders],
  );

  const handleToggleHolderSelection = useCallback(
    async (id: number, checked: boolean) => {
      setSelectedClass(null);

      const newHolders = checked
        ? [...formik.values.shareholder_ids, id]
        : formik.values.shareholder_ids.filter(idx => idx !== id);

      await formik.setFieldValue('shareholder_ids', newHolders);
      await formik.validateField('shareholder_ids');
    },
    [formik],
  );

  const announcementHolders = useMemo(
    () =>
      registerHoldersData?.shareholders.filter(({ id }) =>
        formik.values.shareholder_ids.includes(id),
      ),
    [formik.values.shareholder_ids, registerHoldersData?.shareholders],
  );

  const holderSecurityWarning = useMemo(() => {
    if (!selectedReports?.length || !formik.values?.shareholder_ids || !announcementHolders)
      return null;

    for (const report of selectedReports) {
      if (!report.value.entity_id) continue;

      const holder = registerHoldersData?.shareholders.find(
        ({ entity_id }) => entity_id === report.value.entity_id,
      );
      const foreignHolders = announcementHolders.filter(
        ({ entity_id }) => entity_id !== report.value.entity_id,
      );

      if (!holder || !foreignHolders.length) continue;

      return `
       You have selected a ${lowerCase(report.value.type)} report "${report.label}" for holder ${
        holder.entity.entity_name
      } but in recipients you have ${
        foreignHolders.length === 1
          ? `${foreignHolders[0].entity.entity_name} holder`
          : `${foreignHolders.length} more holders`
      }. It may be private information, are you sure you want to share this?
     `;
    }

    return null;
  }, [
    announcementHolders,
    formik.values?.shareholder_ids,
    registerHoldersData?.shareholders,
    selectedReports,
  ]);

  const classSecurityWarning = useMemo(() => {
    if (!selectedReports?.length || !holdersSubscriptions?.shareholders?.length) return null;

    for (const holder of holdersSubscriptions.shareholders) {
      for (const report of selectedReports) {
        if (!report.value.classes.length) continue;

        const foreignClass = report.value.classes.find(
          rc => !holder.classes.some(hc => hc.id === rc.id),
        );

        if (foreignClass) {
          return `You want to send a report "${report.value.name}",
           which contains information about class ${foreignClass.class_name},
           to holder ${holder.entity.entity_name},
           who is not subscribed to class ${foreignClass.class_name}.`;
        }
      }
    }

    return null;
  }, [holdersSubscriptions?.shareholders, selectedReports]);

  const certificatesSecurityWarning = useMemo<string | null>(() => {
    if (
      !selectedCertificates?.length ||
      !formik.values?.shareholder_ids ||
      !announcementHolders?.length
    ) {
      return null;
    }

    for (const cert of selectedCertificates) {
      if (!cert.value.entity?.id) continue;

      const holder = registerHoldersData?.shareholders.find(
        ({ entity_id }) => entity_id === cert.value.entity?.id,
      );
      const foreignHolders = announcementHolders.filter(
        ({ entity_id }) => entity_id !== cert.value.entity?.id,
      );

      if (!holder || !foreignHolders.length) continue;

      return `
       You have selected a certificate that belongs to "${
         cert.value.entity?.entity_name || cert.value.register_data?.name
       }" but in recipients you have ${
        foreignHolders.length === 1
          ? `${foreignHolders[0].entity.entity_name} holder`
          : `${foreignHolders.length} more holders`
      }. It may be private information, are you sure you want to share this?
     `;
    }

    return null;
  }, [selectedCertificates, announcementHolders]);

  useEffect(() => {
    replaceQuillSizeOptionIcons();
  }, []);

  const securityWarning =
    holderSecurityWarning || classSecurityWarning || certificatesSecurityWarning;
  return (
    <ModalContentWrapper
      open={open}
      title="New Email"
      onClose={handleCloseModalWithResetForm}
      classes={{ dialogPanel: 'lg:!max-w-5xl' }}
    >
      <FormikProvider value={formik}>
        <form className="grid lg:grid-cols-2 gap-4 lg:gap-0" onSubmit={preventFormDefault}>
          <div className="space-y-4 lg:pr-6 lg:border-r">
            <TextInputField
              label="Subtitle"
              name="subject"
              placeholder="Some subject"
              onChange={e => handleChangeSubject(e.target.value)}
            />
            <ReactQuill
              defaultValue={formik.values.text}
              value={formik.values.text}
              modules={modules}
              formats={formats}
              theme="snow"
              onChange={handleChangeText}
              className="[&>.ql-container]:min-h-[250px] [&>.ql-container>.ql-editor]:min-h-[250px]"
            />
            <ReportsSelectPaginate
              label="Reports (Optional)"
              onChange={setSelectedReports}
              value={selectedReports}
              // classNameSelect="z-[4]"
            />
            <CertificatesSelectPaginate
              label="Certificates (Optional)"
              value={selectedCertificates}
              onChange={setSelectedCertificates}
              // classNameSelect="z-[4]"
            />
            {securityWarning && (
              <Alert icon={AlertBlueIcon} variant="neutral-2">
                <div className="whitespace-normal">{securityWarning}</div>
              </Alert>
            )}
            <Button
              loading={isRegistersLoading || isAnnouncementCreation}
              onClick={formik.submitForm}
            >
              Confirm & send
            </Button>
          </div>
          <div className="space-y-4 lg:pl-6">
            <div className="flex gap-6">
              <Select
                label="REGISTER"
                options={registerOptions}
                value={registerId}
                isLoading={isRegistersLoading || isClassHoldersLoading}
                onChange={handleChangeRegister}
                className="z-[3]"
              />
              <SelectPaginate
                label="CLASS (Optional)"
                fixedOption={allClassesOption}
                cacheUniqs={[registerId]}
                debounceTimeoutSearch={700}
                onAsync={handleAsyncRegisterClassList}
                // isLoading={(isRegistersLoading || isClassHoldersLoading) && !!selectedClass?.value}
                value={selectedClass}
                onChange={handleChangeClass}
                className="z-[3]"
              />
            </div>
            <div className="max-h-[450px] relative overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-[2]">
                  <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                    <td className="w-11 pl-3">
                      <CustomCheckbox
                        checked={checkedAllHoldersCheckbox}
                        indeterminate={indeterminateAllHoldersCheckbox}
                        className={classNames(
                          '[&_.ant-checkbox-inner]:w-5',
                          '[&_.ant-checkbox-inner]:h-5',
                          '[&_.ant-checkbox-inner]:!rounded-sm',
                          '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                        )}
                        onChange={e => handleToggleAllHoldersSelection(e.target.checked)}
                      />
                    </td>
                    <td>
                      {formik.values.shareholder_ids.length}{' '}
                      {pluralize('Holder', formik.values.shareholder_ids.length)}
                    </td>
                    <td className="pr-3">ID</td>
                  </tr>
                </thead>
                <tbody>
                  {registerHolders.map(holder => (
                    <tr key={holder.id} className="h-[38px] [&:not(:last-child)]:border-b">
                      <td className="w-11 pl-3">
                        <CustomCheckbox
                          onChange={e => handleToggleHolderSelection(holder.id, e.target.checked)}
                          checked={formik.values.shareholder_ids.includes(holder.id)}
                          className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                        />
                      </td>
                      <td>
                        <Link
                          to={{
                            pathname: issuerDashboardPaths.dynamic.holder(
                              holder.entity_id.toString(),
                            ),
                            search: params.toString(),
                          }}
                          className="text-blue-700 hover:underline"
                        >
                          {holder.entity.entity_name}
                        </Link>
                      </td>
                      <td>{holder.entity.entity_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
